import { useState } from "react";
import { Database, ChevronDown, ChevronUp, Plug, Repeat, PlayCircle } from "lucide-react";

/**
 * Reusable "Data sources & operational guidelines" panel.
 *
 * Renders a collapsible container whose expanded body is a series of cards
 * describing, for each item in `entries`:
 *   - Source (new)       — how the data is captured when new
 *   - Source (existing)  — how the data is captured for existing consumers
 *   - To operationalise  — connectors / policies institutions must put in place
 *
 * `entries` shape: [
 *   {
 *     key: "string",
 *     label: "string",
 *     icon: LucideIcon,
 *     new: { systems: "string", detail: "string" },
 *     existing: { systems: "string", detail: "string" },
 *     operationalise: ["string", ...],
 *   },
 *   ...
 * ]
 */
export function ProvenancePanel({
  entries,
  title = "Data sources & operational guidelines",
  subtitle = "Where each item is drawn from for new and existing consumers, and what institutions must connect to operationalise.",
  testId = "data-provenance",
  defaultOpen = false,
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section
      data-testid={testId}
      className="border border-slate-200 bg-white rounded-sm"
    >
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left"
        data-testid={`${testId}-toggle`}
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-8 h-8 rounded-sm bg-teal-50 border border-teal-200 flex items-center justify-center shrink-0">
            <Database className="w-4 h-4 text-altara-teal" strokeWidth={1.75} />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.22em] text-altara-teal font-semibold">{title}</div>
            <div className="text-sm text-slate-700 leading-snug">{subtitle}</div>
          </div>
        </div>
        <span className="text-[10px] uppercase tracking-[0.15em] text-slate-500 shrink-0 flex items-center gap-1">
          {open ? "Hide details" : "Show details"}
          {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        </span>
      </button>
      {open && (
        <div className="border-t border-slate-100 divide-y divide-slate-100">
          {entries.map(entry => (
            <ProvenanceCard key={entry.key} testid={`${testId}-${entry.key}`} entry={entry} />
          ))}
        </div>
      )}
    </section>
  );
}

export function ProvenanceCard({ testid, entry }) {
  const Icon = entry.icon;
  return (
    <div data-testid={testid} className="p-5">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-7 h-7 rounded-sm bg-slate-100 border border-slate-200 flex items-center justify-center">
          {Icon && <Icon className="w-3.5 h-3.5 text-altara-deep" strokeWidth={2} />}
        </div>
        <div className="font-display font-semibold text-base text-altara-ink">{entry.label}</div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <ProvBlock
          heading="Source · new"
          icon={PlayCircle}
          badge={entry.new.systems}
          body={entry.new.detail}
          tone="teal"
        />
        <ProvBlock
          heading="Source · existing"
          icon={Repeat}
          badge={entry.existing.systems}
          body={entry.existing.detail}
          tone="slate"
        />
        <div className="border border-slate-200 bg-slate-50/50 rounded-sm p-3">
          <div className="flex items-center gap-1.5 mb-2">
            <Plug className="w-3.5 h-3.5 text-altara-deep" />
            <div className="text-[10px] uppercase tracking-[0.18em] text-altara-deep font-semibold">
              To operationalise
            </div>
          </div>
          <ol className="space-y-1.5 text-xs text-slate-700 leading-relaxed list-decimal list-outside ml-4">
            {entry.operationalise.map((step, i) => (
              <li key={i}>{step}</li>
            ))}
          </ol>
        </div>
      </div>
    </div>
  );
}

function ProvBlock({ heading, icon: Icon, badge, body, tone = "teal" }) {
  const toneMap = {
    teal: { icon: "text-altara-teal", border: "border-teal-200", chip: "bg-teal-50 text-teal-800 border-teal-200" },
    slate: { icon: "text-slate-600", border: "border-slate-200", chip: "bg-slate-100 text-slate-700 border-slate-200" },
  };
  const t = toneMap[tone] || toneMap.teal;
  return (
    <div className={`border ${t.border} bg-white rounded-sm p-3`}>
      <div className="flex items-center gap-1.5 mb-2">
        <Icon className={`w-3.5 h-3.5 ${t.icon}`} />
        <div className={`text-[10px] uppercase tracking-[0.18em] font-semibold ${t.icon}`}>{heading}</div>
      </div>
      <div className={`inline-block text-[10px] uppercase tracking-[0.12em] font-medium border px-1.5 py-0.5 rounded-sm mb-2 ${t.chip}`}>
        {badge}
      </div>
      <p className="text-xs text-slate-700 leading-relaxed">{body}</p>
    </div>
  );
}
